import React, { useEffect, useState } from 'react';
import { Card, CardBody, CardHeader } from './ui/Card';
import { CardListToolbar } from './ui/CardListToolbar';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { AlertMessage } from './ui/AlertMessage';
import { ConfirmDialog } from './ui/ConfirmDialog';
import { EmptyState } from './ui/EmptyState';
import { SkeletonRow } from './ui/Skeleton';
import { PartyFormModal } from './PartyFormModal';
import { usePermissions } from '../permissions/PermissionContext';
import { api, extractErrorMessage } from '../api/client';

export function formatPartyAddressSummary(p) {
  if (!p) return '';
  const lines = [p.addressLine1, p.addressLine2].filter((x) => String(x || '').trim());
  const cs = [p.city, p.state].map((x) => String(x || '').trim()).filter(Boolean).join(', ');
  if (cs) lines.push(cs);
  return lines.join(' · ');
}

function PartyMaster({
  title,
  modalTitle,
  addButtonText,
  searchPlaceholder,
  partyCategory,
  namePlaceholder = 'Name',
}) {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [editingItem, setEditingItem] = useState(null);
  const [pageMessage, setPageMessage] = useState('');
  const [pageError, setPageError] = useState('');
  const [confirmDeleteItem, setConfirmDeleteItem] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const { ownerAdmin } = usePermissions();
  const canMutate = ownerAdmin;

  useEffect(() => {
    const fetchParties = async () => {
      setLoading(true);
      setPageError('');
      try {
        const { data } = await api.get(`/api/parties?category=${partyCategory}`);
        setItems(Array.isArray(data) ? data : []);
      } catch (err) {
        setItems([]);
        setPageError(extractErrorMessage(err, 'Unable to load parties'));
      } finally {
        setLoading(false);
      }
    };
    fetchParties();
  }, [partyCategory]);

  useEffect(() => {
    if (!pageMessage) {
      return undefined;
    }
    const timer = setTimeout(() => setPageMessage(''), 3500);
    return () => clearTimeout(timer);
  }, [pageMessage]);

  useEffect(() => {
    if (!pageError) {
      return undefined;
    }
    const timer = setTimeout(() => setPageError(''), 5000);
    return () => clearTimeout(timer);
  }, [pageError]);

  const filteredItems = items.filter((item) => {
    const key = search.trim().toLowerCase();
    if (!key) {
      return true;
    }
    return (
      String(item.name || '').toLowerCase().includes(key) ||
      String(item.contact || '').toLowerCase().includes(key) ||
      String(item.addressLine1 || '').toLowerCase().includes(key) ||
      String(item.addressLine2 || '').toLowerCase().includes(key) ||
      String(item.city || '').toLowerCase().includes(key) ||
      String(item.state || '').toLowerCase().includes(key)
    );
  });

  const openCreateForm = () => {
    setFormMode('create');
    setEditingItem(null);
    setShowForm(true);
  };

  const openEditForm = (record) => {
    setFormMode('edit');
    setEditingItem(record);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setFormMode('create');
    setEditingItem(null);
  };

  const handleSaved = (saved) => {
    const isCreate = formMode === 'create';
    setItems((prev) => (isCreate ? [saved, ...prev] : prev.map((item) => (item.id === saved.id ? saved : item))));
    setPageMessage(formMode === 'create' ? `${title} created successfully` : `${title} updated successfully`);
    closeForm();
  };

  const handleDelete = async () => {
    if (!confirmDeleteItem) {
      return;
    }
    setDeleteLoading(true);
    try {
      await api.delete(`/api/parties/${confirmDeleteItem.id}`);
      const nextItems = items.filter((item) => item.id !== confirmDeleteItem.id);
      setItems(nextItems);
      setPageMessage(`${title} deleted successfully`);
      setConfirmDeleteItem(null);
    } catch (err) {
      setConfirmDeleteItem(null);
      setPageError(extractErrorMessage(err, 'Unable to delete record'));
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div>
      <Card>
        <CardHeader variant="gradient">
          <CardListToolbar
            tone="light"
            title={title}
            actions={
              <>
                <div className="w-full sm:w-64">
                  <Input
                    type="text"
                    placeholder={searchPlaceholder}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    leftIcon={(
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    )}
                  />
                </div>
                {canMutate ? (
                <Button
                  type="button"
                  variant="primary"
                  onClick={openCreateForm}
                  leftIcon={(
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  )}
                >
                  {addButtonText}
                </Button>
                ) : null}
              </>
            }
          />
        </CardHeader>

        <CardBody>
          <AlertMessage
            message={pageMessage}
            type="success"
            onClose={() => setPageMessage('')}
            className="mb-4"
          />
          <AlertMessage
            message={pageError}
            type="error"
            onClose={() => setPageError('')}
            className="mb-4"
          />

          {!loading && filteredItems.length === 0 ? (
            <EmptyState
              title={search ? 'No records match your search' : `No ${String(title).toLowerCase()} yet`}
              description={search ? 'Try a different search term.' : 'Get started by adding your first record.'}
              action={!search && canMutate ? (
                <Button type="button" variant="primary" onClick={openCreateForm}>
                  {addButtonText}
                </Button>
              ) : null}
            />
          ) : (
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
              <table className="table-modern">
                <thead>
                  <tr>
                    <th className="w-14">#</th>
                    <th>Name</th>
                    <th>Contact</th>
                    <th>Address</th>
                    <th className="text-right pr-6">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading
                    ? Array.from({ length: 4 }).map((_, i) => (
                        <SkeletonRow key={i} columns={5} />
                      ))
                    : filteredItems.map((item, index) => (
                        <tr key={item.id}>
                          <td className="text-slate-400 tabular-nums">{index + 1}</td>
                          <td className="font-medium text-slate-800">{item.name || '—'}</td>
                          <td className="text-slate-700 text-sm max-w-[140px]">
                            <span className="block truncate" title={item.contact || ''}>{item.contact || '—'}</span>
                          </td>
                          <td className="text-slate-600 max-w-[360px]">
                            <span className="block truncate" title={formatPartyAddressSummary(item)}>
                              {formatPartyAddressSummary(item) || '—'}
                            </span>
                          </td>
                          <td className="pr-6">
                            <div className="flex items-center justify-end gap-1.5">
                              {canMutate && (
                              <button
                                type="button"
                                onClick={() => openEditForm(item)}
                                className="icon-action icon-action-primary"
                                title="Edit"
                                aria-label={`Edit ${item.name}`}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              )}
                              {canMutate && (
                              <button
                                type="button"
                                onClick={() => {
                                  setPageError('');
                                  setPageMessage('');
                                  setConfirmDeleteItem(item);
                                }}
                                className="icon-action icon-action-danger"
                                title="Delete"
                                aria-label={`Delete ${item.name}`}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <PartyFormModal
        open={showForm}
        onClose={closeForm}
        mode={formMode}
        partyCategory={partyCategory}
        modalTitle={modalTitle}
        namePlaceholder={namePlaceholder}
        record={editingItem}
        onSaved={handleSaved}
        formId="party-master-form"
      />

      <ConfirmDialog
        open={Boolean(confirmDeleteItem)}
        title="Delete Record"
        message={confirmDeleteItem ? `Do you want to delete "${confirmDeleteItem.name}"?` : ''}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setConfirmDeleteItem(null)}
        loading={deleteLoading}
        variant="danger"
      />
    </div>
  );
}

export default PartyMaster;
